'use client'

import { IconCheck, IconLoader2, IconX } from '@tabler/icons-react'
import { Command as CommandPrimitive } from 'cmdk'
import * as React from 'react'
import { cn } from '../utils/cn'
import { Badge } from './Badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from './Command'

export type ComboboxSelectV2Option = {
  description?: string
  keywords?: string[]
  label: string
  value: string
}

const normalizeString = (value: string = '') =>
  value
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const buildOptionSearchValue = (option: ComboboxSelectV2Option) =>
  normalizeString(
    [option.label, option.description, ...(option.keywords ?? [])]
      .filter((value): value is string => Boolean(value))
      .join(' '),
  )

const getSelectedOptions = ({
  options,
  value,
}: {
  options: ComboboxSelectV2Option[]
  value: string[]
}) =>
  value.reduce<ComboboxSelectV2Option[]>((selectedOptions, selectedValue) => {
    const selectedOption = options.find(
      (option) => option.value === selectedValue,
    )

    if (!selectedOption) return selectedOptions

    return [...selectedOptions, selectedOption]
  }, [])

const getFilteredOptions = ({
  options,
  searchValue,
}: {
  options: ComboboxSelectV2Option[]
  searchValue: string
}) => {
  const normalizedSearchValue = normalizeString(searchValue)

  if (!normalizedSearchValue) return options

  return options.filter((option) =>
    buildOptionSearchValue(option).includes(normalizedSearchValue),
  )
}

export function ComboboxSelectV2({
  disabled = false,
  emptyText = 'No se encontraron opciones',
  invalid = false,
  loading = false,
  onSearchValueChange,
  onValueChange,
  options,
  searchPlaceholder = 'Buscar...',
  searchValue,
  selectPlaceholder = 'Selecciona opciones',
  value,
}: {
  disabled?: boolean
  emptyText?: string
  invalid?: boolean
  loading?: boolean
  onSearchValueChange?: (value: string) => void
  onValueChange: (value: string[]) => void
  options: ComboboxSelectV2Option[]
  searchPlaceholder?: string
  searchValue: string
  selectPlaceholder?: string
  value: string[]
}) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [open, setOpen] = React.useState(false)

  const selectedOptions = React.useMemo(
    () => getSelectedOptions({ options, value }),
    [options, value],
  )
  const filteredOptions = React.useMemo(
    () => getFilteredOptions({ options, searchValue }),
    [options, searchValue],
  )

  const focusInput = React.useCallback(() => {
    if (disabled) return

    inputRef.current?.focus()
  }, [disabled])

  const handleInputFocus = React.useCallback(() => {
    if (disabled) return

    setOpen(true)
  }, [disabled])

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const nextFocusedElement = event.relatedTarget

      if (
        nextFocusedElement instanceof Node &&
        containerRef.current?.contains(nextFocusedElement)
      ) {
        return
      }

      setOpen(false)
    },
    [],
  )

  const handleSearchChange = React.useCallback(
    (nextSearchValue: string) => {
      onSearchValueChange?.(nextSearchValue)

      if (open) return

      setOpen(true)
    },
    [onSearchValueChange, open],
  )

  const handleValueToggle = React.useCallback(
    (nextValue: string) => {
      const valueExists = value.some(
        (currentValue) => currentValue === nextValue,
      )
      const nextValues = valueExists
        ? value.filter((currentValue) => currentValue !== nextValue)
        : [...value, nextValue]

      onValueChange(nextValues)
      onSearchValueChange?.('')
      inputRef.current?.focus()
    },
    [onSearchValueChange, onValueChange, value],
  )

  const handleRemoveValue = React.useCallback(
    (valueToRemove: string) => {
      onValueChange(
        value.filter((currentValue) => currentValue !== valueToRemove),
      )
      inputRef.current?.focus()
    },
    [onValueChange, value],
  )

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const shouldRemoveLastValue =
        (event.key === 'Backspace' || event.key === 'Delete') &&
        !searchValue &&
        value.length > 0

      if (shouldRemoveLastValue) {
        onValueChange(value.slice(0, -1))
      }

      if (event.key === 'Escape') {
        inputRef.current?.blur()
        setOpen(false)
      }
    },
    [onValueChange, searchValue, value],
  )

  return (
    <Command
      shouldFilter={false}
      className='h-auto overflow-visible bg-transparent'
    >
      <div ref={containerRef} className='relative' onBlur={handleBlur}>
        <div
          aria-disabled={disabled}
          aria-invalid={invalid}
          className={cn(
            'flex max-h-32 min-h-10 w-full flex-wrap items-start gap-2 overflow-y-auto rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background',
            'focus-within:ring-2 focus-within:ring-ring/75 focus-within:ring-offset-2',
            invalid && 'ring-2 ring-destructive',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          onClick={focusInput}
        >
          {selectedOptions.map((option) => (
            <Badge
              key={option.value}
              className='gap-1 pr-1 text-xs font-medium'
              variant='secondary'
            >
              <span>{option.label}</span>
              <button
                aria-label={`Quitar ${option.label}`}
                className='rounded-full p-0.5 outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2'
                disabled={disabled}
                onClick={() => handleRemoveValue(option.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return

                  handleRemoveValue(option.value)
                }}
                onMouseDown={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
                type='button'
              >
                <IconX className='h-3 w-3 text-muted-foreground hover:text-foreground' />
              </button>
            </Badge>
          ))}

          <CommandPrimitive.Input
            ref={inputRef}
            className='min-w-[10rem] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed'
            disabled={disabled}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
            onValueChange={handleSearchChange}
            placeholder={
              selectedOptions.length > 0 ? searchPlaceholder : selectPlaceholder
            }
            value={searchValue}
          />

          {loading ? (
            <IconLoader2 className='h-4 w-4 animate-spin text-muted-foreground' />
          ) : null}
        </div>

        {open ? (
          <div className='absolute z-50 mt-2 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none'>
            <CommandList withScroll>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => {
                  const isSelected = value.some(
                    (selectedValue) => selectedValue === option.value,
                  )

                  return (
                    <CommandItem
                      key={option.value}
                      className='items-start justify-between gap-3'
                      onMouseDown={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                      }}
                      onSelect={() => handleValueToggle(option.value)}
                      value={option.value}
                    >
                      <div className='space-y-1'>
                        <p className='text-sm font-medium leading-none'>
                          {option.label}
                        </p>
                        {option.description ? (
                          <p className='text-xs text-muted-foreground'>
                            {option.description}
                          </p>
                        ) : null}
                      </div>
                      <IconCheck
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          isSelected ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </div>
        ) : null}
      </div>
    </Command>
  )
}
